"use client"

import { ComponentProps, ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/hooks/use-translation"
import { ArrowDownAZ, ArrowUpAZ, Search, SlidersHorizontal, X } from "lucide-react"

/**
 * Timesheet Hub list language: white row cards (14px radius, hairline border,
 * faint navy shadow, 4px status accent bar on the left) laid out on a grey
 * canvas, with a "Sort:" chip in the toolbar instead of column headers.
 * Values were read off the shipped Timesheet Hub MFE's computed styles.
 */

export type AccentTone = "success" | "error" | "warning" | "info" | "neutral"

const ACCENT_BG: Record<AccentTone, string> = {
  success: "bg-[var(--border-success)]",
  error: "bg-[var(--border-error)]",
  warning: "bg-[var(--th-accent-warning)]",
  info: "bg-[var(--color-primary)]",
  neutral: "bg-[var(--th-chip-border)]",
}

const CHIP_TONE: Record<AccentTone, string> = {
  success: "border-[var(--border-success)] bg-[var(--bg-success)] text-[var(--gw-text-success)]",
  error: "border-[var(--border-error)] bg-[var(--bg-error)] text-[var(--gw-text-error)]",
  warning: "border-[var(--border-warning)] bg-[var(--bg-warning)] text-[var(--gw-text-warning-safe)]",
  info: "border-[var(--border-info)] bg-[var(--bg-info)] text-[var(--gw-text-info)]",
  neutral: "border-[var(--th-chip-border)] bg-[var(--th-meta-bg)] text-[var(--th-meta-text)]",
}

/** The grey canvas the row cards sit on. */
export function RowList({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("space-y-2.5 rounded-[14px] bg-[var(--th-canvas)] p-3", className)}>{children}</div>
}

/** One white row card with a colored left accent bar. */
export function RowCard({ accent, children, className }: { accent: AccentTone; children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-[14px] border border-[var(--th-card-border)] bg-white shadow-[var(--th-card-shadow)]",
        className
      )}
    >
      <div aria-hidden="true" className={cn("absolute inset-y-0 left-0 w-1", ACCENT_BG[accent])} />
      <div className="py-3 pl-5 pr-4">{children}</div>
    </div>
  )
}

/** Worker/record name — Manrope 16 bold, exactly as Timesheet Hub titles rows. */
export function RowName({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("font-heading text-base font-bold text-[var(--gw-text-primary)]", className)}>{children}</span>
}

/** A small-caps meta label + value pair for the row's second line. */
export function RowMeta({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-[0.4px] text-muted-foreground">{label}</div>
      <div className="truncate text-sm text-[var(--th-meta-text)]">{children}</div>
    </div>
  )
}

/** Prominent status chip (the "89 days late" treatment): tinted, bordered, Manrope bold. */
export function StatusChip({ tone, children, title }: { tone: AccentTone; children: ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-[9px] border px-3 py-1.5 font-heading text-sm font-bold",
        CHIP_TONE[tone]
      )}
    >
      {children}
    </span>
  )
}

/**
 * Summary count card (the Worker / Client / Back office row): white 10px-radius
 * card with an icon chip, label, and a Manrope extrabold navy count; the active
 * card gets the navy border. Clicking it applies/clears that quick filter.
 */
export function SummaryCard({
  icon,
  label,
  count,
  active,
  onClick,
}: {
  icon: ReactNode
  label: string
  /** null = the count is unavailable right now — rendered as "—", never a false 0. */
  count: number | null
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 rounded-[10px] border bg-white px-4 py-2.5 text-left",
        active ? "border-[var(--color-primary)] ring-1 ring-[var(--color-primary)]" : "border-[var(--th-chip-border)]"
      )}
    >
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-[var(--bg-info)] text-[var(--color-primary)] [&_svg]:h-4 [&_svg]:w-4"
      >
        {icon}
      </span>
      <span className={cn("min-w-0 flex-1 truncate text-sm", active ? "font-bold" : "font-medium")}>{label}</span>
      <span className="font-heading text-xl font-extrabold text-[var(--color-primary)]">{count ?? "—"}</span>
    </button>
  )
}

/** The "Filters" toolbar chip that opens the filter drawer, with an active-filter count badge. */
export function FiltersButton({
  activeCount,
  expanded,
  onClick,
}: {
  activeCount: number
  expanded: boolean
  onClick: () => void
}) {
  const { t } = useTranslation()
  return (
    <Button variant="outline" className="gap-1.5" aria-expanded={expanded} onClick={onClick}>
      <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
      {t("common.filters")}
      {activeCount > 0 && (
        <span className="ml-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-primary)] px-1 text-xs font-semibold text-white">
          {activeCount}
        </span>
      )}
    </Button>
  )
}

/** Search input with the leading magnifier, at the 260px usability minimum. */
export function SearchBox({
  className,
  ...props
}: ComponentProps<typeof Input>) {
  return (
    <div className={cn("relative min-w-[260px]", className)}>
      <Search aria-hidden="true" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input {...props} className="pl-9" />
    </div>
  )
}

export interface ChipOption {
  value: string
  label: string
  count?: number
}

/** Single-select pill chip group (the WORKER TYPE chips): navy when active. */
export function ChoiceChips({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: ChipOption[]
  value: string
  onChange: (value: string) => void
  ariaLabel: string
}) {
  return (
    <div role="group" aria-label={ariaLabel} className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(o.value)}
            className={cn(
              "rounded-full px-3.5 py-1.5 text-sm",
              active
                ? "bg-[var(--color-primary)] font-semibold text-white"
                : "border border-[var(--th-chip-border)] bg-white text-[var(--th-chip-text)]"
            )}
          >
            {o.label}
            {o.count !== undefined && (
              <span className={cn("ml-1.5 text-xs", active ? "text-white/70" : "text-muted-foreground")}>
                {o.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/** One labeled section inside the filter drawer (TIMESHEET METHOD / WORKER TYPE …). */
export function FilterSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h4 className="th-section-label">{label}</h4>
      {children}
    </section>
  )
}

export interface ActiveFilterChip {
  key: string
  label: string
  onRemove: () => void
}

/** The ACTIVE chip row under the toolbar: removable chips + "Clear all". */
export function ActiveFilters({ chips, onClearAll }: { chips: ActiveFilterChip[]; onClearAll: () => void }) {
  const { t } = useTranslation()
  if (chips.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="th-section-label">{t("common.activeFilters")}</span>
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center gap-1 rounded-full bg-[var(--bg-info)] py-1 pl-3 pr-1.5 text-sm text-[var(--gw-text-info)]"
        >
          {chip.label}
          <button
            type="button"
            aria-label={t("common.removeFilterAria", { label: chip.label })}
            className="rounded-full p-0.5 hover:bg-black/5"
            onClick={chip.onRemove}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </span>
      ))}
      <Button variant="ghost" size="sm" onClick={onClearAll}>
        {t("common.clearAll")}
      </Button>
    </div>
  )
}

export interface SortOption<K extends string> {
  key: K
  label: string
}

/** "Sort: <field>" toolbar chip + direction toggle, replacing column headers. */
export function SortChip<K extends string>({
  options,
  sortKey,
  asc,
  onKeyChange,
  onDirectionToggle,
}: {
  options: SortOption<K>[]
  sortKey: K
  asc: boolean
  onKeyChange: (key: K) => void
  onDirectionToggle: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-1">
      <Select value={sortKey} onValueChange={(v) => onKeyChange(v as K)}>
        <SelectTrigger className="h-8 w-[190px] text-sm" aria-label={t("common.sortByAria")}>
          <span className="text-muted-foreground">{t("common.sortPrefix")}&nbsp;</span>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.key} value={o.key}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        variant="outline"
        size="icon"
        className="h-8 w-8"
        aria-label={asc ? t("common.sortedAscending") : t("common.sortedDescending")}
        onClick={onDirectionToggle}
      >
        {asc ? <ArrowUpAZ className="h-4 w-4" /> : <ArrowDownAZ className="h-4 w-4" />}
      </Button>
    </div>
  )
}
